import {createStore, combineReducers, applyMiddleware} from 'redux';
import createSagaMiddleware, {END} from 'redux-saga';
import {reducer as formReducer} from 'redux-form';
import {composeWithDevTools} from 'redux-devtools-extension';
import * as reducers from '../modules';
import {sagas as QuestionsSagas} from '../modules/Questions';
import {sagas as QuestionSagas} from '../modules/Question';
import {sagas as TagsSagas} from '../modules/Tags';
import {sagas as AnswerSagas} from '../modules/Question-Answers';
import {sagas as CommentSagas} from '../modules/Question-Comments';
import {sagas as NavigationSagas} from '../modules/Navigation';
import {sagas as AuthenticateSagas, createMiddleware as createAuthMiddleware} from '../modules/Authenticate';
import {sagas as ContextualSagas} from '../modules/Contextual';
import {sagas as SearchSagas} from '../modules/Search';

import QaClient from '../lib/qa-client';

const isServerSide = !global.document;

const readCookie = (k) => {
  const cookie = global.document && global.document.cookie;
  return cookie && (cookie.match(`(^|; )${k}=([^;]*)`) || 0)[2];
};

function getRootSagas({initialState, fetch, history, headers}) {
  const svcQaApi = isServerSide ? initialState.urls.svcQaServiceApi : initialState.urls.svcQaRelativeApi;
  const qaClient = QaClient({
    fetch,
    svcQaApi,
    isServerSide,
    headers,
  });
  const currentUserCookie = readCookie('TESCookieUser');
  const {watchGetCurrentUser} = AuthenticateSagas({qaClient, currentUserCookie});
  const {watchContextualListsSaga} = ContextualSagas({qaClient});
  const {
    watchShowHome,
    watchShowTag,
    watchShowQuestion,
    watchShowNewQuestion,
    watchShowEditQuestion,
  } = NavigationSagas(history);
  const {watchGetQuestions, watchSetOrder} = QuestionsSagas({qaClient});
  const {watchSearchQuestions} = SearchSagas({qaClient, history});
  const {
    watchGetQuestion,
    watchCreateQuestion,
    watchUpdateQuestion,
    watchModerateQuestion,
    watchLockQuestion,
    watchDeleteQuestion,
    watchIncrementQuestionCounter,
    watchGetQuestionVotes,
    watchGetQuestionAutoCreate,
  } = QuestionSagas({qaClient});
  const {
    watchIncrementAnswerCounter,
    watchCreateAnswer,
    watchUpdateAnswer,
    watchModerateAnswer,
    watchAcceptAnswer,
  } = AnswerSagas({qaClient});
  const {
    watchAddComment,
    watchCreateComment,
    watchUpdateComment,
    watchModerateComment,
  } = CommentSagas({qaClient});
  const {
    watchGetTags,
  } = TagsSagas({qaClient});

  function *rootSagas() {
    yield [
      watchShowHome(),
      watchShowTag(),
      watchShowQuestion(),
      watchShowNewQuestion(),
      watchShowEditQuestion(),
      watchGetQuestions(),
      watchGetQuestion(),
      watchGetTags(),
      watchGetQuestionAutoCreate(),
      watchSetOrder(),
      watchCreateQuestion(),
      watchUpdateQuestion(),
      watchModerateQuestion(),
      watchDeleteQuestion(),
      watchLockQuestion(),
      watchCreateAnswer(),
      watchUpdateAnswer(),
      watchAcceptAnswer(),
      watchModerateAnswer(),
      watchIncrementQuestionCounter(),
      watchIncrementAnswerCounter(),
      watchGetQuestionVotes(),
      watchGetCurrentUser(),
      watchAddComment(),
      watchCreateComment(),
      watchUpdateComment(),
      watchModerateComment(),
      watchContextualListsSaga(),
      watchSearchQuestions(),
    ];
  }
  return rootSagas;
}

function configureStore({initialState}) {
  const sagaMiddleware = createSagaMiddleware();
  const authMiddleware = createAuthMiddleware(global);
  const reducer = combineReducers({...reducers, form: formReducer});
  const store = createStore(
    reducer,
    initialState,
    composeWithDevTools(
      applyMiddleware(authMiddleware, sagaMiddleware)
    )
  );

  // Add methods that allow server side rendering to work
  // (wavehands) magic
  store.close = () => store.dispatch(END);
  store.runSaga = sagaMiddleware.run;

  return store;
}

function createStoreAndRunSagas({initialState, fetch, history}) {
  const store = configureStore({initialState});
  const rootSagas = getRootSagas({initialState, fetch, history});
  store.runSaga(rootSagas);
  return store;
}

export {getRootSagas, configureStore, createStoreAndRunSagas};
